import React, { createContext, useContext, useMemo } from 'react';

import type { LensDataset, LensDatasetColumn, LensGovernedMeasureMetadata } from '~/types/leLens';

type MeasureMetadata = { savedDatasets?: Array<LensDataset>; metadata?: LensGovernedMeasureMetadata };
const Context = createContext<MeasureMetadata>({});

/** Read metadata only: editor callbacks and write requests keep their original datasets. */
export function GovernedMeasureMetadataProvider({
  children,
  savedDatasets,
  metadata,
}: MeasureMetadata & { children: React.ReactNode }) {
  const value = useMemo(() => ({ savedDatasets, metadata }), [savedDatasets, metadata]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function effectiveGovernedMeasureColumns(
  dataset: LensDataset | undefined,
  { savedDatasets, metadata }: MeasureMetadata,
): Array<LensDatasetColumn> | undefined {
  if (!dataset || dataset.source?.kind !== 'governed' || dataset.override) return dataset?.columns;
  const saved = savedDatasets?.find((item) => item.id === dataset.id);
  if (
    !saved ||
    saved.source?.kind !== dataset.source.kind ||
    saved.source.catalogueId !== dataset.source.catalogueId ||
    saved.source.table !== dataset.source.table ||
    saved.source.version !== dataset.source.version ||
    saved.sql !== dataset.sql
  )
    return dataset.columns;
  const corrections = metadata?.datasets.find((item) => item.id === dataset.id)?.columns;
  if (!corrections?.length) return dataset.columns;
  return dataset.columns?.map((column) => {
    const original = saved.columns?.find((item) => item.name === column.name);
    const correction = corrections.find((item) => item.name === column.name);
    if (
      !correction ||
      !original ||
      column.kind !== 'measure' ||
      original.kind !== column.kind ||
      original.type !== column.type ||
      original.aggregate !== column.aggregate ||
      JSON.stringify(original.ratio) !== JSON.stringify(column.ratio) ||
      JSON.stringify(original.format) !== JSON.stringify(column.format) ||
      dataset.fields?.some(
        (field) =>
          field.name === column.name || field.name === correction.ratio?.of || field.name === correction.ratio?.per,
      )
    )
      return column;
    return {
      ...column,
      ...(correction.aggregate ? { aggregate: correction.aggregate } : {}),
      ...(correction.ratio ? { ratio: correction.ratio } : {}),
      ...(correction.format ? { format: correction.format } : {}),
    };
  });
}

export function useEffectiveGovernedMeasureColumns(dataset: LensDataset | undefined) {
  const metadata = useContext(Context);
  return useMemo(() => effectiveGovernedMeasureColumns(dataset, metadata), [dataset, metadata]);
}
